import { useMemo, useEffect, useState, useRef } from 'react';
import { read, utils } from 'xlsx';
import {
  FileUpload,
  Button,
  FileItem,
  Modal,
  Spinner,
  Container,
  Card,
  CardHeader,
  Link,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogActions,
  Typography,
} from '@airbus/components-react';
import CloseIcon from '@mui/icons-material/Close';
import styles from '../components/Upload.module.css';
import { useColumnContextAPI } from '../context/GetAllColumnContext';
import { useLanguageContextAPI } from '../context/LanguageContext';
import { useUserContextApi } from '../context/UserContext';
import { DESKTOP_BREAKPOINT, MOBILE_MEDIA_QUERY } from './breakpoints';
import { useDraggableResizableDialog } from '../components/grid/viewManager/useDraggableResizableDialog';

export type uploadCommitHandler = (props: any) => Promise<any>;
export type CommitHandler = (props: any) => Promise<any>;

export type UploaderProps = {
  [key: string]: any;
  featureCommitHandler: uploadCommitHandler | CommitHandler;
};

const featureSheetMap = {
  upload_htz_list: 'MoB_Upload',
  upload_costing_opex: 'IMP_COSTING_OPEX',
  upload_offer_opex: 'IMP_OFFER_OPEX',
};

export default function Uploader(props: UploaderProps) {
  const {
    openFileUpload,
    setOpenFileUpload,
    setAlertbar,
    value,
    editedRowOriginal,
    allRows,
    featureCommitHandler,

    setOpenResults,
    setResultData,
    templateUrl,
  } = props;
  const { t } = useLanguageContextAPI();
  const { userDetails } = useUserContextApi();
  const [files, setFiles] = useState<Array<FileItem>>([]);
  const [openIcon, setOpenIcon] = useState(true);
  const [disableUploadButton, setDisableUploadButton] = useState(true);
  const { allAccessibleColumns, updatereRender, tableDetails, allDropdowns } =
    useColumnContextAPI();
  const [openSpinner, setOpenSpinner] = useState(false);
  const [workbook, setWorkbook] = useState(null);
  const [startProcessing, setStartProcessing] = useState(false);

  const featureName = value?.featureName;
  const sheetTabName = featureSheetMap[featureName as keyof typeof featureSheetMap];

  const featureLabel = <Typography variant='h6'>{t(`header_actions.${featureName ?? 'upload'}`, {
    defaultValue: t('upload'),
  })}</Typography>;

  const featureDisplayName = t(`header_actions.${featureName ?? 'upload'}`, {
    defaultValue: t('upload'),
  });

  const [isMobileView, setIsMobileView] = useState<boolean>(() =>
    typeof window === 'undefined' ? false : window.matchMedia(MOBILE_MEDIA_QUERY).matches,
  );
  const [isDesktop, setIsDesktop] = useState<boolean>(() =>
    typeof window === 'undefined'
      ? true
      : window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`).matches,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mqMobile = window.matchMedia(MOBILE_MEDIA_QUERY);
    const onMobileChange = (e: MediaQueryListEvent) => setIsMobileView(e.matches);
    mqMobile.addEventListener?.('change', onMobileChange);
    mqMobile.addListener?.(onMobileChange);

    const mqDesktop = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`);
    const onDesktopChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mqDesktop.addEventListener?.('change', onDesktopChange);
    mqDesktop.addListener?.(onDesktopChange);

    setIsMobileView(mqMobile.matches);
    setIsDesktop(mqDesktop.matches);

    return () => {
      mqMobile.removeEventListener?.('change', onMobileChange);
      mqMobile.removeListener?.(onMobileChange);
      mqDesktop.removeEventListener?.('change', onDesktopChange);
      mqDesktop.removeListener?.(onDesktopChange);
    };
  }, []);

  // ---------- Dragging ----------
  const wrapperRef = useRef<HTMLDivElement>(null);

  // IMPORTANT: clamp inside viewport + no hardcoded init sizes
  const { dlgRect, onHeaderMouseDown, setDlgSize } = useDraggableResizableDialog({
    minWidth: 200,
    minHeight: 120,
  });
  useEffect(() => {
    if (!wrapperRef.current) return;

    const measure = () => {
      const rect = wrapperRef.current!.getBoundingClientRect();
      setDlgSize(rect.width, rect.height);
    };
    measure();

    let ro: ResizeObserver | null = null;
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver((entries) => {
        const cr = entries[0].contentRect;
        setDlgSize(cr.width, cr.height);
      });
      ro.observe(wrapperRef.current);
    }

    window.addEventListener('resize', measure);

    return () => {
      window.removeEventListener('resize', measure);
      ro?.disconnect();
    };
  }, [openFileUpload, isDesktop, setDlgSize]);

  const constrainedPosition = useMemo(() => {
    if (typeof window === 'undefined') return { left: dlgRect.left, top: dlgRect.top };

    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    const measuredWidth =
      dlgRect.width || (wrapperRef.current?.getBoundingClientRect().width ?? dlgRect.width);
    const measuredHeight =
      dlgRect.height || (wrapperRef.current?.getBoundingClientRect().height ?? dlgRect.height);

    const maxLeft = Math.max(0, viewportW - measuredWidth);
    const maxTop = Math.max(0, viewportH - measuredHeight);

    const left = Math.min(Math.max(dlgRect.left, 0), maxLeft);
    const top = Math.min(Math.max(dlgRect.top, 0), maxTop);

    return { left, top };
  }, [dlgRect.left, dlgRect.top, dlgRect.width, dlgRect.height]);

  const desktopWrapperStyle = useMemo(() => {
    if (!isDesktop) return undefined;
    return {
      position: 'fixed' as const,
      top: constrainedPosition.top,
      left: constrainedPosition.left,
      zIndex: 1000,
      boxSizing: 'border-box' as const,
    };
  }, [isDesktop, constrainedPosition]);

  // ---------- File Upload Logic ----------
  const dropFile = (newFiles) => validateFile(newFiles);

  function validateFile(newFiles) {
    const validExts = ['.xlsx', '.xls'];
    let fileExt = newFiles[0].data.name;
    fileExt = fileExt.substring(fileExt.lastIndexOf('.'));
    const { id, data } = newFiles[0];
    if (validExts.indexOf(fileExt) < 0) {
      setFiles([
        {
          id: id,
          data: data,
          errorMsg: `${t('fileUpload.only_excel')}`,
          percentage: 0,
          status: 'error',
          retry: false,
        },
      ]);
      return false;
    }

    const fileReader = new FileReader();
    fileReader.onload = (e) => {
      const bufferArray = e.target.result;
      const wb = read(bufferArray, { type: 'buffer', cellDates: true });
      if (wb.SheetNames.indexOf(sheetTabName) === -1) {
        setDisableUploadButton(true);
        setFiles([
          {
            id: id,
            data: data,
            errorMsg: t('fileUpload.no_template_found', { msg: featureDisplayName }),
            percentage: 0,
            status: 'error',
            retry: false,
          },
        ]);
      } else {
        setWorkbook(wb);
        setDisableUploadButton(false);
        setOpenSpinner(true);
      }
    };
    fileReader.onerror = (error) => {
      setFiles([
        {
          id: id,
          data: data,
          errorMsg: error?.target?.error?.message
            ? t('error.error_msg', { msg: error?.target?.error?.message })
            : t('error.error_while_loading'),
          percentage: 0,
          status: 'error',
          retry: false,
        },
      ]);
    };
    fileReader.onloadend = () => setOpenSpinner(false);

    fileReader.readAsArrayBuffer(newFiles[0].data);
    setFiles(newFiles);
  }
  const handleDeleteFile = (id: number) => {
    const fileList = [...files];
    const index = fileList.findIndex((file) => file.id === id);
    fileList.splice(index, 1);
    setFiles(fileList);
    setDisableUploadButton(true);
  };
  const handleUpload = () => {
    setOpenSpinner(true);
  };

  useEffect(() => {
    if (openSpinner && workbook) {
      setStartProcessing(true);
    }
  }, [openSpinner, workbook]);

  useEffect(() => {
    if (startProcessing) {
      uploadData();
    }
    async function uploadData() {
      try {
        const excelData = workbook.SheetNames.reduce((data, item) => {
          const sheetData = {};
          sheetData[item] = utils.sheet_to_json(workbook.Sheets[item], { UTC: true });
          data.push(sheetData);
          return data;
        }, []);

        const sheetData = excelData.filter((item) => {
          if (Object.keys(item)[0] === sheetTabName) {
            return true;
          }
        });
        const commitResult = await featureCommitHandler({
          sheetData,
          uploadedRows: sheetData[0]?.[sheetTabName],
          featureName,
          allAccessibleColumns,
          updatereRender,
          tableDetails,
          allDropdowns,
          setAlertbar,
          t,
          allRows,
          userDetails,
          excelData,
          editedRowOriginal,
        });
        setResultData(commitResult);
        setOpenIcon(false);
        updatereRender();
      } catch (error) {
        setOpenSpinner(false);
        setAlertbar({
          children: t('error.action_failed_following', { msg: error.message }),
          severity: 'error',
        });
      } finally {
        setStartProcessing(false);
        setWorkbook(null);
        setOpenSpinner(false);
        setOpenResults(true);
        setOpenFileUpload(false);
      }
    }
  }, [startProcessing]);

  const UploadContent = (
    <>
      {openIcon && (
        <>
          <DialogContent className={styles.uploadDialogContent}>
            <FileUpload
              dragAndDrop
              onAdd={(newFiles) => dropFile(newFiles)}
              onDelete={handleDeleteFile}
              files={files}
              multiple={false}
            />
            {openSpinner && (
              <div className={styles.spinnerWheel}>
                <Spinner size='medium' label={t('please_wait')} className='innerCircle' />
              </div>
            )}
            {templateUrl && (
              <div className={styles.link}>
                <Link variant='underlined' size='small' href={templateUrl} target='_blank'>
                  {t('fileUpload.template_file')}
                </Link>
              </div>
            )}
          </DialogContent>
          <DialogActions className={styles.uploadDialogActions}>
            <Button variant='secondary' onClick={() => setOpenFileUpload(false)}>
              {t('cancel')}
            </Button>
            <Button disabled={disableUploadButton} variant='primary' onClick={handleUpload}>
              {t('upload')}
            </Button>
          </DialogActions>
        </>
      )}
    </>
  );

  if (isMobileView) {
    return (
      <Dialog open={openFileUpload} onClose={() => setOpenFileUpload(false)}>
        <DialogHeader className={styles.dialogheader}>{featureLabel}</DialogHeader>
        <DialogContent className={styles.dialogContentCentered}>
          <div className={styles.fileUploadWrapper}>
            <FileUpload
              dragAndDrop
              onAdd={(newFiles) => dropFile(newFiles)}
              onDelete={handleDeleteFile}
              files={files}
              multiple={false}
            />
          </div>
          {openSpinner && (
            <div className={styles.spinnerWheel}>
              <Spinner size='medium' label={t('please_wait')} className='innerCircle' />
            </div>
          )}
          {templateUrl && (
            <div className={styles.linkLeft}>
              <Link variant='underlined' size='small' href={templateUrl} target='_blank' className={styles.linkBlue}>
                {t('fileUpload.template_file')}
              </Link>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={disableUploadButton} variant='primary' onClick={handleUpload}>
            {t('upload')}
          </Button>
          <Button variant='secondary' onClick={() => setOpenFileUpload(false)}>
            {t('cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Modal open={openFileUpload}>
      <Container className={styles.cardContainer}>
        <div ref={wrapperRef} className={styles.draggableWrapper} style={desktopWrapperStyle}>
          <Card className={`${styles.card} ${isDesktop ? styles.cardDesktop : ''}`}>
            {/* Drag handle */}
            <div
              onMouseDown={isDesktop ? onHeaderMouseDown : undefined}
              className={isDesktop ? styles.dragHandleDesktop : undefined}
            >
              <CardHeader
                title={featureDisplayName}
                className={styles.cardHeader}
                action={
                  <Button
                    variant='ghost'
                    size='small'
                    className={styles.closeIcon}
                    onClick={() => setOpenFileUpload(false)}
                  >
                    <CloseIcon />
                  </Button>
                }
              />
            </div>

            {UploadContent}
          </Card>
        </div>
      </Container>
    </Modal>
  );
}